/**
 * CaseloadTransferModal - 2-step wizard to bulk-reassign clients from one clinician to another
 * Step 1: Select from/to clinicians + choose which clients to transfer
 * Step 2: Confirm & execute
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import styled from 'styled-components';
import { ArrowRightLeft, AlertTriangle, CheckCircle } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import clientService, {
  type RedDotDisposition,
  type TransferCaseloadPreviewResult,
} from '../../services/api/clientService';
import { getAllActiveMembers } from '../../services/api/userService';
import { useTransferCaseloadPreview } from '../../hooks/useClients';
import { RedDotDispositionOptions } from '../../components/clients/RedDotDispositionOptions';
import {
  CLIENT_STATUS_BADGE_VARIANT,
  CLIENT_STATUS_LABEL,
} from './clientFormConstants';

// ============================================================================
// Props
// ============================================================================

interface CaseloadTransferModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// Styled Components
// ============================================================================

const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  margin-bottom: ${({ theme }) => theme.spacing[6]};
`;

const StepDot = styled.div<{ $active: boolean; $done: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  flex-shrink: 0;
  background: ${({ theme, $active, $done }) => {
    if ($done) return theme.colors.status.success.bg;
    if ($active) return theme.colors.primary.base;
    return theme.colors.backgrounds.gray;
  }};
  color: ${({ theme, $active, $done }) => {
    if ($done) return theme.colors.status.success.text;
    if ($active) return theme.colors.text.white;
    return theme.colors.text.secondary;
  }};
  border: 1px solid
    ${({ theme, $active, $done }) => {
      if ($done) return theme.colors.status.success.border;
      if ($active) return theme.colors.primary.base;
      return theme.colors.borders.light;
    }};
`;

const StepConnector = styled.div<{ $done: boolean }>`
  flex: 1;
  height: 2px;
  background: ${({ theme, $done }) =>
    $done ? theme.colors.status.success.border : theme.colors.borders.light};
`;

const SectionTitle = styled.h4`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[4]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing[4]};
  margin-bottom: ${({ theme }) => theme.spacing[5]};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing[2]};
`;

const SelectField = styled.select`
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing[3]};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgrounds.white};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary.base};
    box-shadow: ${({ theme }) => theme.shadows.ring};
  }

  &:disabled {
    background: ${({ theme }) => theme.colors.backgrounds.gray};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const ClientListSection = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  overflow: hidden;
`;

const ClientListHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.backgrounds.gray};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
`;

const ClientListHeaderLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
`;

const ClientListBody = styled.div`
  max-height: 280px;
  overflow-y: auto;
`;

const ClientRow = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[3]} ${theme.spacing[4]}`};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }

  &:hover {
    background: ${({ theme }) => theme.colors.backgrounds.page};
  }
`;

const ClientName = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

const ClientListFooter = styled.div`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[4]}`};
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-align: right;
`;

const LoadingState = styled.div`
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const EmptyState = styled.div`
  padding: ${({ theme }) => theme.spacing[6]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const TransferSummaryBox = styled.div`
  padding: ${({ theme }) => theme.spacing[4]};
  background: ${({ theme }) => theme.colors.status.warning.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.warning.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const TransferSummaryTitle = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.status.warning.text};
  margin: 0 0 ${({ theme }) => theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`;

const TransferSummaryText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.warning.text};
  margin: 0;
`;

const ResultBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

const ResultIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ResultTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const ResultSubtext = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

const ErrorText = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};
  margin: ${({ theme }) => theme.spacing[2]} 0 0;
`;

const RedDotSummary = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing[3]};
`;

const RedDotBreakdownList = styled.ul`
  list-style: none;
  margin: 0 0 ${({ theme }) => theme.spacing[5]};
  padding: 0;
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  max-height: 180px;
  overflow-y: auto;
`;

const RedDotBreakdownItem = styled.li`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  color: ${({ theme }) => theme.colors.text.secondary};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[4]};
`;

const FooterRight = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
`;

// ============================================================================
// Helpers
// ============================================================================

const formatMemberOption = (m: { firstName: string; lastName: string; roles?: { name: string }[] }) =>
  `${m.firstName} ${m.lastName}${m.roles?.[0] ? ` — ${m.roles[0].name}` : ''}`;

// ============================================================================
// Component
// ============================================================================

type Step = 1 | 2 | 3 | 4; // 3 = Red Dot disposition (conditional), 4 = done

export const CaseloadTransferModal = ({ isOpen, onClose }: CaseloadTransferModalProps) => {
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>(1);
  const [fromMemberId, setFromMemberId] = useState('');
  const [toMemberId, setToMemberId] = useState('');
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [transferredCount, setTransferredCount] = useState(0);
  const [executeError, setExecuteError] = useState<string | null>(null);
  const [disposition, setDisposition] = useState<RedDotDisposition>('TRANSFER_TO_NEW');
  const [redDotPreview, setRedDotPreview] = useState<TransferCaseloadPreviewResult | null>(null);

  const { data: allMembers = [], isLoading: membersLoading } = useQuery({
    queryKey: ['members-all-active'],
    queryFn: getAllActiveMembers,
    enabled: isOpen,
  });

  const { data: fromClients = [], isLoading: clientsLoading } = useQuery({
    queryKey: ['clients-by-assignee', fromMemberId],
    queryFn: () => clientService.getClientsByAssignee(fromMemberId),
    enabled: isOpen && !!fromMemberId,
  });

  const previewMutation = useTransferCaseloadPreview();

  const transferMutation = useMutation({
    mutationFn: () =>
      clientService.transferCaseload({
        fromMemberId,
        toMemberId,
        clientIds: Array.from(selectedClientIds),
        redDotDisposition: redDotPreview ? disposition : undefined,
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      queryClient.invalidateQueries({ queryKey: ['clients-by-assignee'] });
      setTransferredCount(result.transferred);
      setStep(4);
    },
    onError: () => {
      setExecuteError('Transfer failed. Please try again.');
    },
  });

  /**
   * On the final "Transfer" click: preview the aggregate open-Red-Dot impact first.
   * 0 open Red Dots ⇒ execute immediately (no extra step). Otherwise show step 3
   * (disposition picker) before actually executing the transfer.
   */
  const handleReviewTransfer = () => {
    setExecuteError(null);
    previewMutation.mutate(
      { fromMemberId, clientIds: Array.from(selectedClientIds) },
      {
        onSuccess: (result) => {
          if (result.openRedDotCount > 0) {
            setRedDotPreview(result);
            setStep(3);
          } else {
            transferMutation.mutate();
          }
        },
        onError: () => setExecuteError('Failed to preview the transfer. Please try again.'),
      },
    );
  };

  const toMemberOptions = useMemo(
    () => allMembers.filter((m) => m.id !== fromMemberId),
    [allMembers, fromMemberId],
  );
  const fromMember = useMemo(
    () => allMembers.find((m) => m.id === fromMemberId),
    [allMembers, fromMemberId],
  );
  const toMember = useMemo(
    () => allMembers.find((m) => m.id === toMemberId),
    [allMembers, toMemberId],
  );
  const allSelected = fromClients.length > 0 && selectedClientIds.size === fromClients.length;
  const canProceed = !!fromMemberId && !!toMemberId && selectedClientIds.size > 0;

  const handleFromMemberChange = (id: string) => {
    setFromMemberId(id);
    setSelectedClientIds(new Set());
    if (id === toMemberId) setToMemberId('');
  };

  const handleToggleAll = () => {
    if (allSelected) {
      setSelectedClientIds(new Set());
    } else {
      setSelectedClientIds(new Set(fromClients.map((c) => c.id)));
    }
  };

  const handleToggleClient = (clientId: string) => {
    setSelectedClientIds((prev) => {
      const next = new Set(prev);
      if (next.has(clientId)) {
        next.delete(clientId);
      } else {
        next.add(clientId);
      }
      return next;
    });
  };

  const handleClose = () => {
    setStep(1);
    setFromMemberId('');
    setToMemberId('');
    setSelectedClientIds(new Set());
    setTransferredCount(0);
    setExecuteError(null);
    setDisposition('TRANSFER_TO_NEW');
    setRedDotPreview(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={step === 4 ? 'Transfer Complete' : 'Transfer Caseload'}
      size="md"
    >
      {/* Step Indicator (steps 1–3; step 3 only appears when open Red Dots are found) */}
      {step < 4 && (
        <StepIndicator>
          <StepDot $active={step === 1} $done={step > 1}>
            {step > 1 ? '✓' : '1'}
          </StepDot>
          <StepConnector $done={step > 1} />
          <StepDot $active={step === 2} $done={step > 2}>
            {step > 2 ? '✓' : '2'}
          </StepDot>
          {step === 3 && (
            <>
              <StepConnector $done={false} />
              <StepDot $active $done={false}>
                3
              </StepDot>
            </>
          )}
        </StepIndicator>
      )}

      {/* ── Step 1: Configure Transfer ── */}
      {step === 1 && (
        <>
          <SectionTitle>Configure Transfer</SectionTitle>

          <FormGrid>
            <div>
              <FieldLabel htmlFor="from-member">From clinician</FieldLabel>
              <SelectField
                id="from-member"
                value={fromMemberId}
                onChange={(e) => handleFromMemberChange(e.target.value)}
                disabled={membersLoading}
              >
                <option value="">
                  {membersLoading ? 'Loading...' : 'Select clinician...'}
                </option>
                {allMembers.map((m) => (
                  <option key={m.id} value={m.id}>
                    {formatMemberOption(m)}
                  </option>
                ))}
              </SelectField>
            </div>

            <div>
              <FieldLabel htmlFor="to-member">To clinician</FieldLabel>
              <SelectField
                id="to-member"
                value={toMemberId}
                onChange={(e) => setToMemberId(e.target.value)}
                disabled={!fromMemberId || membersLoading}
              >
                <option value="">Select clinician...</option>
                {toMemberOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {formatMemberOption(m)}
                  </option>
                ))}
              </SelectField>
            </div>
          </FormGrid>

          {fromMemberId && (
            <>
              <SectionTitle>Select clients to transfer</SectionTitle>

              {clientsLoading ? (
                <LoadingState>Loading clients...</LoadingState>
              ) : fromClients.length === 0 ? (
                <EmptyState>No clients assigned to this clinician.</EmptyState>
              ) : (
                <ClientListSection>
                  <ClientListHeader>
                    <input
                      type="checkbox"
                      id="select-all-clients"
                      checked={allSelected}
                      onChange={handleToggleAll}
                    />
                    <ClientListHeaderLabel htmlFor="select-all-clients">
                      Select all
                    </ClientListHeaderLabel>
                  </ClientListHeader>

                  <ClientListBody>
                    {fromClients.map((client) => (
                      <ClientRow key={client.id}>
                        <input
                          type="checkbox"
                          checked={selectedClientIds.has(client.id)}
                          onChange={() => handleToggleClient(client.id)}
                        />
                        <ClientName>
                          {client.firstName} {client.lastName}
                        </ClientName>
                        <Badge variant={CLIENT_STATUS_BADGE_VARIANT[client.status]}>
                          {CLIENT_STATUS_LABEL[client.status]}
                        </Badge>
                      </ClientRow>
                    ))}
                  </ClientListBody>

                  <ClientListFooter>
                    {selectedClientIds.size} of {fromClients.length} client
                    {fromClients.length !== 1 ? 's' : ''} selected
                  </ClientListFooter>
                </ClientListSection>
              )}
            </>
          )}

          <ModalFooter>
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                onClick={() => setStep(2)}
                disabled={!canProceed}
              >
                Next
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 2: Confirm & Execute ── */}
      {step === 2 && (
        <>
          <SectionTitle>Confirm Transfer</SectionTitle>

          <TransferSummaryBox>
            <TransferSummaryTitle>
              <AlertTriangle />
              Review before proceeding
            </TransferSummaryTitle>
            <TransferSummaryText>
              Transfer{' '}
              <strong>
                {selectedClientIds.size} client{selectedClientIds.size !== 1 ? 's' : ''}
              </strong>{' '}
              from{' '}
              <strong>
                {fromMember?.firstName} {fromMember?.lastName}
              </strong>{' '}
              to{' '}
              <strong>
                {toMember?.firstName} {toMember?.lastName}
              </strong>
              . Client assignments and clinician relationships will be reassigned.
            </TransferSummaryText>
          </TransferSummaryBox>

          {executeError && <ErrorText>{executeError}</ErrorText>}

          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setExecuteError(null);
                setStep(1);
              }}
            >
              Back
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                leftIcon={<ArrowRightLeft />}
                onClick={handleReviewTransfer}
                isLoading={previewMutation.isPending || transferMutation.isPending}
                disabled={previewMutation.isPending || transferMutation.isPending}
              >
                Transfer
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 3: Red Dot disposition (only when the preview finds open Red Dots) ── */}
      {step === 3 && redDotPreview && (
        <>
          <SectionTitle>Open Red Dots</SectionTitle>

          <RedDotSummary>
            <strong>
              {redDotPreview.openRedDotCount} open Red Dot
              {redDotPreview.openRedDotCount !== 1 ? 's' : ''}
            </strong>{' '}
            across {redDotPreview.clientsWithOpenRedDots.length} client
            {redDotPreview.clientsWithOpenRedDots.length !== 1 ? 's' : ''} in this transfer. Choose
            how to handle them.
          </RedDotSummary>

          <RedDotBreakdownList>
            {redDotPreview.clientsWithOpenRedDots.map((c) => (
              <RedDotBreakdownItem key={c.clientId}>
                <span>
                  {c.firstName} {c.lastName}
                </span>
                <span>
                  {c.count} open Red Dot{c.count !== 1 ? 's' : ''}
                </span>
              </RedDotBreakdownItem>
            ))}
          </RedDotBreakdownList>

          <RedDotDispositionOptions
            value={disposition}
            onChange={setDisposition}
            openRedDotCount={redDotPreview.openRedDotCount}
            newOwnerName={toMember ? `${toMember.firstName} ${toMember.lastName}` : 'the new BCBA'}
            outgoingOwnerName={
              fromMember ? `${fromMember.firstName} ${fromMember.lastName}` : 'the outgoing BCBA'
            }
          />

          {executeError && <ErrorText>{executeError}</ErrorText>}

          <ModalFooter>
            <Button
              variant="secondary"
              onClick={() => {
                setExecuteError(null);
                setStep(2);
              }}
            >
              Back
            </Button>
            <FooterRight>
              <Button
                variant="primary"
                leftIcon={<ArrowRightLeft />}
                onClick={() => transferMutation.mutate()}
                isLoading={transferMutation.isPending}
                disabled={transferMutation.isPending}
              >
                Transfer
              </Button>
            </FooterRight>
          </ModalFooter>
        </>
      )}

      {/* ── Step 4: Done ── */}
      {step === 4 && (
        <>
          <ResultBanner>
            <ResultIcon>
              <CheckCircle />
            </ResultIcon>
            <ResultTitle>Transfer complete</ResultTitle>
            <ResultSubtext>
              {transferredCount} client{transferredCount !== 1 ? 's' : ''} successfully
              transferred from{' '}
              <strong>
                {fromMember?.firstName} {fromMember?.lastName}
              </strong>{' '}
              to{' '}
              <strong>
                {toMember?.firstName} {toMember?.lastName}
              </strong>
              .
            </ResultSubtext>
          </ResultBanner>

          <ModalFooter>
            <div />
            <Button variant="primary" onClick={handleClose}>
              Done
            </Button>
          </ModalFooter>
        </>
      )}
    </Modal>
  );
};

export default CaseloadTransferModal;
